// ============================================================
// SensorsActuators.jsx
//
// Course content page for the "Sensors & Actuators" lesson
// (IoT track). Rewritten to:
//   1) fix a stray placeholder-text bug that was rendering
//      literally on the page instead of showing a diagram,
//   2) replace the fake/incomplete Arduino example with a real,
//      complete sketch that actually compiles,
//   3) give students REAL components to buy (not just category
//      names) plus a basic wiring guide, so the lesson matches
//      the track's goal: buy the part -> wire it up -> write
//      firmware that runs a live, physical example,
//   4) match the app-wide dark-purple/goldenrod look used in
//      IoTDashboard.jsx, so every IoT page shares one identity,
//   5) use arrow-function components and Material Icons only,
//      per the project's clean-code conventions.
//
// Brand palette (matches IoTDashboard.jsx's PageWrapper/
// DashboardGlassCard/ViewButton):
//   Background  -> #100720   (dark purple, same as the dashboard)
//   Surface     -> rgba(16,7,32,0.85)
//   Primary     -> goldenrod (sensors / inputs)
//   Accent      -> #2DD4BF   (teal — actuators / outputs; the
//                             "third color" alongside purple+gold)
//   Text        -> #FAFAF9
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Alert,
} from "@mui/material";

// ── Icons — @mui/icons-material only, matching the rest of the app ──
import SensorsIcon from "@mui/icons-material/Sensors";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import BoltIcon from "@mui/icons-material/Bolt";
import SyncIcon from "@mui/icons-material/Sync";
import TerminalIcon from "@mui/icons-material/Terminal";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CableIcon from "@mui/icons-material/Cable";
import MemoryIcon from "@mui/icons-material/Memory";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// ── Brand tokens ──────────────────────────────────────────────
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
};

// Real, buyable parts for this lesson — not just category names.
// Prices are rough ranges (component cost varies by supplier/region),
// kept deliberately approximate rather than pinned to figures that
// will go stale.
const SHOPPING_LIST = [
    { part: "LM35 analog temperature sensor", use: "Reads temperature as a simple analog voltage", price: "$1–3" },
    { part: "DHT22 digital temperature/humidity sensor", use: "More accurate alternative to the LM35; needs the DHT library", price: "$4–8" },
    { part: "HC-SR04 ultrasonic distance sensor", use: "Measures distance via sound-wave echo time", price: "$2–4" },
    { part: "MQ-2 gas/smoke sensor", use: "Detects combustible gas and smoke concentration", price: "$2–5" },
    { part: "SG90 micro servo motor", use: "Precise position control (0–180°), e.g. a vent flap", price: "$2–5" },
    { part: "5V single-channel relay module", use: "Lets a microcontroller switch a higher-power load on/off", price: "$1–3" },
    { part: "L298N motor driver + small DC motor", use: "Drives a DC motor (e.g. a fan) in both directions", price: "$3–6" },
];

// Sub-components (arrow-function style, defined before first use)

const CategoryHeader = ({ icon, label, color }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box
            sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                background: `linear-gradient(135deg, ${color}33, transparent)`,
                border: `1px solid ${color}66`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            {React.cloneElement(icon, { style: { color, fontSize: 22 } })}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 800, color: BRAND.white }}>
            {label}
        </Typography>
    </Box>
);

const ComponentCard = ({ title, icon, examples }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            background: BRAND.surface,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 4,
            transition: "0.3s",
            height: "100%",
            "&:hover": { borderColor: BRAND.gold, transform: "translateY(-4px)" },
        }}
    >
        <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {React.cloneElement(icon, { style: { color: BRAND.gold, fontSize: 24 } })}
                <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700 }}>
                    {title}
                </Typography>
            </Box>
            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
            <Grid container spacing={1}>
                {examples.map((example) => (
                    <Grid item xs={6} key={example}>
                        <Chip
                            label={example}
                            size="small"
                            sx={{
                                bgcolor: "rgba(255,255,255,0.05)",
                                color: BRAND.muted,
                                border: "1px solid rgba(255,255,255,0.1)",
                                width: "100%",
                            }}
                        />
                    </Grid>
                ))}
            </Grid>
        </Stack>
    </Paper>
);

const SectionHeader = ({ icon, label }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        {React.cloneElement(icon, { style: { color: BRAND.gold, fontSize: 20 } })}
        <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.white }}>
            {label}
        </Typography>
    </Box>
);

// Shows the actual signal flow (sensor -> microcontroller -> actuator)
// in place of the old broken "[Image comparing...]" placeholder text.
const SignalFlowDiagram = () => {
    const nodeStyle = {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        p: 2.5,
        borderRadius: 3,
        background: "rgba(255,255,255,0.03)",
        border: `1px solid ${BRAND.border}`,
        minWidth: 140,
    };

    return (
        <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ my: 6, flexWrap: "wrap" }}
        >
            <Box sx={nodeStyle}>
                <SensorsIcon sx={{ color: BRAND.gold, fontSize: 32 }} />
                <Typography variant="body2" sx={{ color: BRAND.white, fontWeight: 700 }}>
                    Sensor
                </Typography>
                <Typography variant="caption" sx={{ color: BRAND.muted, textAlign: "center" }}>
                    Reads a physical value (e.g. temperature)
                </Typography>
            </Box>

            <ArrowForwardIcon sx={{ color: BRAND.muted, transform: { xs: "rotate(90deg)", sm: "none" } }} />

            <Box sx={nodeStyle}>
                <MemoryIcon sx={{ color: BRAND.white, fontSize: 32 }} />
                <Typography variant="body2" sx={{ color: BRAND.white, fontWeight: 700 }}>
                    Microcontroller
                </Typography>
                <Typography variant="caption" sx={{ color: BRAND.muted, textAlign: "center" }}>
                    Runs your firmware, makes the decision
                </Typography>
            </Box>

            <ArrowForwardIcon sx={{ color: BRAND.muted, transform: { xs: "rotate(90deg)", sm: "none" } }} />

            <Box sx={nodeStyle}>
                <BoltIcon sx={{ color: BRAND.teal, fontSize: 32 }} />
                <Typography variant="body2" sx={{ color: BRAND.white, fontWeight: 700 }}>
                    Actuator
                </Typography>
                <Typography variant="caption" sx={{ color: BRAND.muted, textAlign: "center" }}>
                    Acts on the real world (e.g. switches a fan)
                </Typography>
            </Box>
        </Stack>
    );
};

const LogicFlow = ({ code }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API isn't available in every browser/context —
            // fail silently rather than breaking the page.
        }
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden" }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(0,0,0,0.3)" }}>
                <Typography variant="caption" sx={{ color: BRAND.gold, fontWeight: 700, fontFamily: "monospace" }}>
                    CONTROL_LOGIC.ino
                </Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Typography component="pre" sx={{ p: 2.5, color: "#FCD34D", fontSize: 13, fontFamily: "'Fira Code', monospace", overflowX: "auto" }}>
                {code}
            </Typography>
        </Box>
    );
};

// A real, complete Arduino sketch — not a stand-in function name.
// Uses an LM35 (10mV per °C) and a relay-driven fan, matching the
// shopping list and wiring steps below.
const ARDUINO_SKETCH = `// Automated Cooling System
// Hardware: LM35 temperature sensor on A0, 5V relay module on D7.

const int TEMP_SENSOR_PIN = A0;
const int RELAY_PIN = 7;
const float TEMP_THRESHOLD_C = 30.0;

void setup() {
  pinMode(RELAY_PIN, OUTPUT);
  Serial.begin(9600);
}

void loop() {
  int rawValue = analogRead(TEMP_SENSOR_PIN);        // 0-1023
  float voltage = rawValue * (5.0 / 1023.0);          // convert to volts
  float temperatureC = voltage * 100.0;               // LM35: 10mV per °C

  if (temperatureC > TEMP_THRESHOLD_C) {
    digitalWrite(RELAY_PIN, HIGH);                    // energize relay -> fan ON
    Serial.print("Fan ON - Temp: ");
  } else {
    digitalWrite(RELAY_PIN, LOW);                     // de-energize relay -> fan OFF
    Serial.print("Fan OFF - Temp: ");
  }
  Serial.println(temperatureC);

  delay(1000);
}`;

// ── Main Page ───────────────────────────────────────────────────

const SensorsActuators = () => (
    <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
        <Container maxWidth="lg">
            {/* HERO SECTION */}
            <Box sx={{ mb: 10, textAlign: "center" }}>
                <Avatar sx={{ bgcolor: BRAND.gold, width: 64, height: 64, mx: "auto", mb: 3, boxShadow: `0 0 30px ${BRAND.gold}66` }}>
                    <SensorsIcon sx={{ fontSize: 32, color: BRAND.bg }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 2 }}>
                    Sensors & <span style={{ color: BRAND.teal }}>Actuators</span>
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, mx: "auto", lineHeight: 1.8 }}>
                    The "nervous system" of a machine. Sensors perceive environmental data, while
                    actuators translate digital commands into physical force. By the end of this
                    lesson you'll know which real parts to buy, how to wire them to a microcontroller,
                    and how to write firmware that reacts to the physical world.
                </Typography>
            </Box>

            {/* WHAT YOU'LL NEED — addresses "buy the component" */}
            <Box sx={{ mb: 10 }}>
                <SectionHeader icon={<ShoppingCartIcon />} label="What You'll Need" />
                <Typography sx={{ color: BRAND.muted, mb: 3 }}>
                    These are real, inexpensive parts available from any electronics supplier
                    (e.g. SparkFun, Adafruit, or a generic Arduino starter-kit seller). Prices are
                    approximate per unit.
                </Typography>
                <Paper elevation={0} sx={{ background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 3, overflow: "hidden" }}>
                    {SHOPPING_LIST.map((item, i) => (
                        <Box
                            key={item.part}
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                justifyContent: "space-between",
                                gap: 1,
                                px: 3,
                                py: 2,
                                borderBottom: i < SHOPPING_LIST.length - 1 ? `1px solid ${BRAND.border}` : "none",
                            }}
                        >
                            <Box>
                                <Typography sx={{ color: BRAND.white, fontWeight: 700 }}>{item.part}</Typography>
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>{item.use}</Typography>
                            </Box>
                            <Chip
                                label={item.price}
                                size="small"
                                sx={{ bgcolor: "rgba(218,165,32,0.12)", color: BRAND.gold, fontWeight: 700, alignSelf: { xs: "flex-start", sm: "center" } }}
                            />
                        </Box>
                    ))}
                </Paper>
            </Box>

            {/* SENSORS VS ACTUATORS */}
            <Grid container spacing={4} sx={{ mb: 8 }}>
                <Grid item xs={12} md={6}>
                    <CategoryHeader icon={<SettingsInputComponentIcon />} label="Input: Sensors" color={BRAND.gold} />
                    <ComponentCard
                        title="Environmental Data"
                        icon={<ThermostatIcon />}
                        examples={["LM35 (temp)", "DHT22 (temp/humidity)", "HC-SR04 (distance)", "MQ-2 (gas)"]}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <CategoryHeader icon={<BoltIcon />} label="Output: Actuators" color={BRAND.teal} />
                    <ComponentCard
                        title="Physical Response"
                        icon={<GraphicEqIcon />}
                        examples={["SG90 servo", "DC motor + L298N", "5V relay module", "Piezo buzzer"]}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 8 }} />

            {/* SETTING UP THE CIRCUIT — addresses "set up the system" */}
            <Box sx={{ mb: 10 }}>
                <SectionHeader icon={<CableIcon />} label="Setting Up the Circuit" />
                <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                    This lesson's worked example is an automated fan: an LM35 reads the temperature,
                    and once it crosses a threshold, a relay switches a fan on.
                </Typography>
                <Stack spacing={1.5} sx={{ mb: 3 }}>
                    {[
                        "LM35: left pin -> 5V, middle pin -> A0, right pin -> GND.",
                        "Relay module: VCC -> 5V, GND -> GND, IN -> digital pin 7.",
                        "Relay COM + NO terminals wire into the fan's own power line — the fan runs on its own supply, not through the Arduino.",
                    ].map((step, i) => (
                        <Stack direction="row" spacing={2} key={step}>
                            <Chip
                                label={i + 1}
                                size="small"
                                sx={{ bgcolor: "rgba(218,165,32,0.15)", color: BRAND.gold, fontWeight: 700, minWidth: 28 }}
                            />
                            <Typography variant="body2" sx={{ color: BRAND.muted }}>{step}</Typography>
                        </Stack>
                    ))}
                </Stack>
                <Alert
                    severity="warning"
                    icon={<WarningAmberIcon />}
                    sx={{ bgcolor: "rgba(245,158,11,0.08)", color: BRAND.white, border: "1px solid rgba(245,158,11,0.3)" }}
                >
                    If the fan (or any load) runs on mains voltage (110–240V AC), use a relay module
                    rated for that voltage and treat all AC wiring with care. If you're new to this,
                    practice first with a low-voltage DC fan or an LED instead of a mains appliance.
                </Alert>
            </Box>

            <SignalFlowDiagram />

            {/* THE CONTROL LOOP — addresses "write the firmware" */}
            <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                <Grid item xs={12} md={5}>
                    <SectionHeader icon={<SyncIcon />} label="The Control Loop" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                        In an intelligent system, these components form a <strong>closed loop</strong>:
                        the sensor provides feedback, the microcontroller makes a decision, and the
                        actuator adjusts the environment.
                    </Typography>
                    <Stack spacing={2}>
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(218,165,32,0.06)", border: `1px solid ${BRAND.gold}4d` }}>
                            <Typography variant="subtitle2" sx={{ color: BRAND.gold }}>Step 1: Sense</Typography>
                            <Typography variant="caption" sx={{ color: BRAND.muted }}>Read the LM35's analog voltage and convert it to °C.</Typography>
                        </Box>
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(45,212,191,0.06)", border: `1px solid ${BRAND.teal}4d` }}>
                            <Typography variant="subtitle2" sx={{ color: BRAND.teal }}>Step 2: Actuate</Typography>
                            <Typography variant="caption" sx={{ color: BRAND.muted }}>Switch the relay, which switches the fan.</Typography>
                        </Box>
                    </Stack>
                </Grid>
                <Grid item xs={12} md={7}>
                    <LogicFlow code={ARDUINO_SKETCH} />
                </Grid>
            </Grid>

            {/* LEARNING OUTCOMES — mapped to buy / set up / build firmware */}
            <Box sx={{ mb: 10, p: 5, borderRadius: 6, background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 4, textAlign: "center" }}>
                    By the End of This Lesson, You Can
                </Typography>
                <Grid container spacing={3}>
                    {[
                        "Choose the right sensor or actuator for a task, and know roughly what it costs",
                        "Wire a sensor and a relay-driven actuator to a microcontroller safely",
                        "Convert a raw analog reading into a real-world unit (e.g. volts to °C)",
                        "Write and upload a complete Arduino sketch with working setup() and loop()",
                        "Apply threshold-based decision logic to trigger a physical actuator",
                        "Follow basic electrical safety practice when a load runs on mains voltage",
                    ].map((goal) => (
                        <Grid item xs={12} md={4} key={goal}>
                            <Stack direction="row" spacing={2}>
                                <DoneIcon sx={{ color: BRAND.teal }} />
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>{goal}</Typography>
                            </Stack>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* FOOTER CTA */}
            <Box
                sx={{
                    p: 8,
                    borderRadius: 8,
                    textAlign: "center",
                    background: "rgba(255,255,255,0.03)",
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
                }}
            >
                <TerminalIcon sx={{ fontSize: 40, color: BRAND.gold, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Hardware Ready?</Typography>
                <Typography sx={{ color: BRAND.muted, mb: 4 }}>Connect your sensors and watch your code come to life.</Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                    <Chip label="Start Interactive Lab" onClick={() => {}} sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 800, px: 3, py: 2.5 }} />
                    <Chip label="Component Datasheets" variant="outlined" onClick={() => {}} sx={{ borderColor: BRAND.muted, color: BRAND.white }} />
                </Stack>
            </Box>
        </Container>
    </Box>
);

export default SensorsActuators;
